import { useCallback, useState } from 'react';
import { apiInstance } from '../../services/Api';
import { getCurrentReturnUrl, isLocalReturnUrl, TOKEN_EXPIRE_ENDPOINT } from './authUtilities';
import { AuthStatus } from './types';

export interface UseAuthStatusResult {
	/** The latest result, or null before the first check. */
	status: AuthStatus | null;
	loading: boolean;
	/** Message from the latest failed check, otherwise null. */
	error: string | null;
	/**
	 * Asks the CMS whether the user is logged in. The CMS picks the cookie scheme from the host.
	 * `returnUrl` is a local path to come back to after login, e.g. `/calculator?step=3`; it defaults to the
	 * current path and query. Rejects on failure, after also setting `error`.
	 */
	checkStatus: (returnUrl?: string) => Promise<AuthStatus>;
}

export const useAuthStatus = (): UseAuthStatusResult => {
	const [status, setStatus] = useState<AuthStatus | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const checkStatus = useCallback(async (returnUrl?: string): Promise<AuthStatus> => {
		setLoading(true);
		setError(null);
		try {
			const effectiveReturnUrl = returnUrl ?? getCurrentReturnUrl();
			if (!isLocalReturnUrl(effectiveReturnUrl)) {
				throw new Error(String.raw`Return URL must be a local path starting with "/" (not "//" or "/\")`);
			}
			const query = new URLSearchParams({ returnUrl: effectiveReturnUrl });
			// The shared axios instance resolves with the response body (see services/Api).
			const result = await apiInstance().get<AuthStatus, AuthStatus>(
				`${TOKEN_EXPIRE_ENDPOINT}?${query.toString()}`,
				{ withCredentials: true, headers: { 'Cache-Control': 'no-store' } }
			);
			setStatus(result);
			return result;
		} catch (error_) {
			setError(error_ instanceof Error ? error_.message : String(error_));
			throw error_;
		} finally {
			setLoading(false);
		}
	}, []);

	return { status, loading, error, checkStatus };
};
